/**
 * Monta tudo que o app precisa: banco local, ações, API, sincronização e sessão.
 * A web passa Storage (Dexie) e fetch; o nativo passará Storage (SQLite) e fetch.
 */
import { createStore, type StoreApi } from 'zustand/vanilla';
import { ApiClient, type FetchLike } from '../api/client';
import { LocalDb } from '../db/local-db';
import type { Professional } from '../schemas/entities';
import type { Storage } from '../storage/storage';
import { SyncEngine } from '../sync/engine';
import { LindezaActions } from './actions';
import { semearExemplo as semearNoBanco } from './exemplo';

export type SessionMode = 'none' | 'account';

export interface SessionState {
  ready: boolean;
  mode: SessionMode;
  professionalId: string | null;
  token: string | null;
  email: string | null;
}

export const META_SESSION = 'session';

export interface RuntimeOptions {
  storage: Storage;
  fetch: FetchLike;
  /** origem da API e do link público ("" = mesma origem) */
  apiBaseUrl: string;
  /** origem usada nos links enviados aos clientes (ex.: https://app.lindeza.app) */
  publicBaseUrl: string;
  deviceName?: string;
  now?: () => number;
  onInvalidRecord?: (entity: string, record: unknown, issues: unknown) => void;
  onRemoteChanges?: ConstructorParameters<typeof SyncEngine>[2]['onRemoteChanges'];
}

export interface LindezaRuntime {
  storage: Storage;
  db: LocalDb;
  actions: LindezaActions;
  api: ApiClient;
  sync: SyncEngine;
  session: StoreApi<SessionState>;
  publicBaseUrl: string;
  init(): Promise<void>;
  requestLogin(email: string, name?: string): ReturnType<ApiClient['requestMagicLink']>;
  verifyLogin(input: { token: string } | { email: string; code: string }): Promise<Professional>;
  /** Cadastro com senha: cria a conta e abre a sessão. */
  register(input: { name: string; email: string; password: string; source?: string; campaign?: string; ref?: string }): Promise<Professional>;
  /** Entrada por e-mail e senha. */
  loginWithPassword(email: string, password: string): Promise<Professional>;
  /**
   * Agenda de exemplo para os testes de ponta a ponta. Não há caminho até
   * aqui pela interface -- ver app/exemplo.ts.
   */
  semearExemplo(): Promise<void>;
  logout(options?: { everywhere?: boolean }): Promise<void>;
  /** Atualiza o perfil direto do servidor (slug, assinatura). */
  refreshProfile(): Promise<Professional | null>;
}

export function createRuntime(options: RuntimeOptions): LindezaRuntime {
  const { storage } = options;
  const session = createStore<SessionState>(() => ({
    ready: false,
    mode: 'none',
    professionalId: null,
    token: null,
    email: null,
  }));

  const db = new LocalDb(storage, {
    getProfessionalId: () => session.getState().professionalId,
    ...(options.now ? { now: options.now } : {}),
    ...(options.onInvalidRecord ? { onInvalidRecord: options.onInvalidRecord } : {}),
  });
  const api = new ApiClient({
    baseUrl: options.apiBaseUrl,
    fetch: options.fetch,
    getToken: () => session.getState().token,
  });
  const actions = new LindezaActions({ db });

  async function saveSession(next: Omit<SessionState, 'ready'>): Promise<void> {
    await storage.setMeta(META_SESSION, next);
    session.setState({ ...next, ready: true });
  }

  /*
   * Fixture dos testes de ponta a ponta: não existe conta em servidor nenhum,
   * então a sincronização fica desligada. Não é um modo de sessão -- o app vê
   * uma conta comum, e só este sinalizador, que nenhuma tela lê, a separa.
   */
  let modoFixture = false;

  const sync = new SyncEngine(db, api, {
    enabled: () => !modoFixture && session.getState().mode === 'account' && Boolean(session.getState().token),
    onUnauthorized: () => {
      // Sessão revogada em outro aparelho: mantém os dados e pede para entrar de novo.
      void saveSession({ ...session.getState(), token: null });
    },
    ...(options.onRemoteChanges ? { onRemoteChanges: options.onRemoteChanges } : {}),
  });

  /**
   * Guarda a sessão e carrega o perfil. É o mesmo final para as três portas de
   * entrada -- senha, cadastro e link mágico --, e por isso mora num lugar só:
   * esquecer o storage.clear() numa delas deixaria dados de outra conta no
   * aparelho.
   */
  async function abrirSessao(res: { token: string; professional: Professional }): Promise<Professional> {
    const current = session.getState();
    // Outra conta: começa do zero no aparelho.
    if (current.mode !== 'account' || current.professionalId !== res.professional.id) {
      await storage.clear();
    }
    await saveSession({
      mode: 'account',
      professionalId: res.professional.id,
      token: res.token,
      email: res.professional.email ?? null,
    });
    const saved = await db.applyRemote('professional', res.professional);
    if (!saved) throw new Error('Não deu para carregar seu perfil. Tente de novo em instantes.');
    void sync.sync();
    return saved;
  }

  const runtime: LindezaRuntime = {
    storage,
    db,
    actions,
    api,
    sync,
    session,
    publicBaseUrl: options.publicBaseUrl,

    async init() {
      const saved = await storage.getMeta<Omit<SessionState, 'ready'>>(META_SESSION);
      session.setState({
        ready: true,
        mode: saved?.mode ?? 'none',
        professionalId: saved?.professionalId ?? null,
        token: saved?.token ?? null,
        email: saved?.email ?? null,
      });
      await sync.init();
    },

    requestLogin(email, name) {
      return api.requestMagicLink(email.trim().toLowerCase(), name);
    },

    async verifyLogin(input) {
      return abrirSessao(await api.verify(input, options.deviceName));
    },

    async register(input) {
      return abrirSessao(await api.register({ ...input, email: input.email.trim().toLowerCase() }, options.deviceName));
    },

    async loginWithPassword(email, password) {
      return abrirSessao(await api.loginWithPassword(email.trim().toLowerCase(), password, options.deviceName));
    },

    async semearExemplo() {
      modoFixture = true;
      await storage.clear();
      /*
       * O token é de mentira e nunca é usado: com modoFixture a sincronização
       * não roda. Mas precisa existir, senão o app entende que a sessão
       * expirou e pede o código de novo.
       */
      const professionalId = await semearNoBanco(db, options.now ? options.now() : Date.now(), async (id) => {
        await saveSession({ mode: 'account', professionalId: id, token: 'fixture-e2e', email: null });
      });
      session.setState({ professionalId });
    },

    async logout(opts = {}) {
      const { mode, token } = session.getState();
      if (mode === 'account' && token) {
        try {
          if (opts.everywhere) await api.logoutAll();
          else await api.logout();
        } catch {
          // Sem internet: o token expira sozinho no servidor; aqui apagamos tudo mesmo assim.
        }
      }
      await storage.clear();
      session.setState({ mode: 'none', professionalId: null, token: null, email: null, ready: true });
    },

    async refreshProfile() {
      if (session.getState().mode !== 'account') return null;
      try {
        const res = await api.me();
        return await db.applyRemote('professional', res.professional);
      } catch {
        return null;
      }
    },
  };
  return runtime;
}
